import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Mail, ArrowLeft, Loader2, CheckCircle, ShieldCheck, FlaskConical, Copy, Check, User, Gift, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ThemeToggle } from '@/components/shared/ThemeToggle';
import { getBackendUrl } from '@/config';

const API_URL = getBackendUrl();

export function OTPLogin({ onSuccess, onBack }) {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'profile' | 'success'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [testMode, setTestMode] = useState(false);
  const [testOtp, setTestOtp] = useState('');
  const [copied, setCopied] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  
  // Profile completion form state
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    referralCode: ''
  });
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const [validatingReferral, setValidatingReferral] = useState(false);
  
  const otpInputRefs = useRef([]);

  // Check for referral code in URL on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setProfileData(prev => ({ ...prev, referralCode: refCode.toUpperCase() }));
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.length < 8) {
      setReferralValid(null);
      setReferrerName('');
      return;
    }

    setValidatingReferral(true);
    try {
      const response = await fetch(`${API_URL}/api/referrals/validate/${code}`);
      const data = await response.json();
      
      setReferralValid(data.valid);
      if (data.valid) {
        setReferrerName(data.referrer_name || 'A Kosha Tax user');
      } else {
        setReferrerName('');
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
      setReferralValid(false);
    } finally {
      setValidatingReferral(false);
    }
  };

  // Handle referral code change
  const handleReferralCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setProfileData(prev => ({ ...prev, referralCode: code }));
    
    if (code.length >= 8) {
      validateReferralCode(code);
    } else {
      setReferralValid(null);
      setReferrerName('');
    }
  };

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      
      // Focus the next empty input or the last one
      const nextIndex = Math.min(index + digits.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
    } else {
      // Single digit input
      const newOtp = [...otp];
      newOtp[index] = value.replace(/\D/g, '');
      setOtp(newOtp);
      
      // Auto-focus next input
      if (value && index < 5) {
        otpInputRefs.current[index + 1]?.focus();
      }
    }
    setError('');
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Send OTP
  const handleSendOtp = async (e) => {
    e?.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send OTP');
      }

      // Check if we're in test mode (Resend sandbox limitation)
      if (data.test_mode && data.test_otp) {
        setTestMode(true);
        setTestOtp(data.test_otp);
        toast.info('Test Mode: OTP displayed below for development testing');
      } else {
        setTestMode(false);
        setTestOtp('');
        toast.success('Verification code sent to your email!');
      }

      setStep('otp');
      setResendCooldown(60);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.toLowerCase().trim(),
          code: otpCode 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid verification code');
      }

      // Check if new user needs profile completion
      if (data.needs_profile) {
        setStep('profile');
        toast.success('Email verified! Please complete your profile.');
        return;
      }

      // Existing user - login successful
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setStep('success');
      toast.success('Welcome back!');

      // Callback with user data
      setTimeout(() => {
        onSuccess?.(data);
      }, 1500);

    } catch (err) {
      setError(err.message);
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Complete profile for new users
  const handleCompleteProfile = async (e) => {
    e?.preventDefault();
    
    if (!profileData.firstName.trim() || !profileData.lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/otp/complete-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          first_name: profileData.firstName.trim(),
          last_name: profileData.lastName.trim(),
          referral_code: profileData.referralCode.trim() || null
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to complete profile');
      }

      // Store token and user
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      setStep('success');
      
      // Show referral message if applicable
      if (data.referral?.processed) {
        toast.success(`Welcome! You were referred by ${data.referral.referrer_name}. Complete your first filing to earn your bonus!`, {
          duration: 6000
        });
      } else {
        toast.success('Account created successfully!');
      }
      
      setTimeout(() => {
        onSuccess?.(data);
      }, 1500);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-verify when all digits entered
  useEffect(() => {
    if (step === 'otp' && otp.every(d => d !== '') && !loading) {
      handleVerifyOtp();
    }
  }, [otp, step]);

  // Resend OTP
  const handleResend = () => {
    if (resendCooldown > 0) return;
    setOtp(['', '', '', '', '', '']);
    handleSendOtp();
  };

  if (step === 'success') {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">You're in!</h3>
        <p className="text-muted-foreground">Redirecting to your dashboard...</p>
      </div>
    );
  }

  // Profile completion step for new users
  if (step === 'profile') {
    return (
      <form onSubmit={handleCompleteProfile} className="space-y-4">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <User className="h-7 w-7 text-primary" />
          </div>
          <h3 className="text-lg font-semibold">Complete Your Profile</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Just a few details to get started
          </p>
        </div>

        {/* Referral banner if valid */}
        {referralValid && referrerName && (
          <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
            <Gift className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              <span className="font-semibold">{referrerName}</span> invited you! 
              Complete your first filing to earn a <span className="font-bold">$50 bonus</span>.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              type="text"
              placeholder="John"
              value={profileData.firstName}
              onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
              disabled={loading}
              required
              autoFocus
              data-testid="profile-first-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              type="text"
              placeholder="Doe"
              value={profileData.lastName}
              onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
              disabled={loading}
              required
              data-testid="profile-last-name"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="referralCode" className="flex items-center gap-2">
            <Gift className="h-4 w-4" />
            Referral Code (Optional)
          </Label>
          <div className="relative">
            <Input
              id="referralCode"
              type="text"
              placeholder="REF-XXXXXXXX"
              value={profileData.referralCode}
              onChange={handleReferralCodeChange}
              disabled={loading}
              className={`uppercase ${
                referralValid === true ? 'border-green-500 focus:ring-green-500' : 
                referralValid === false ? 'border-red-500 focus:ring-red-500' : ''
              }`}
              data-testid="profile-referral-code"
            />
            {validatingReferral && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!validatingReferral && referralValid === true && (
              <CheckCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-500" />
            )}
            {!validatingReferral && referralValid === false && profileData.referralCode && (
              <AlertCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-red-500" />
            )}
          </div>
          {referralValid === true && referrerName && (
            <p className="text-xs text-green-600">Referred by {referrerName}</p>
          )}
          {referralValid === false && profileData.referralCode && (
            <p className="text-xs text-red-500">Invalid referral code</p>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button 
          type="submit" 
          className="w-full" 
          disabled={loading || !profileData.firstName.trim() || !profileData.lastName.trim()}
          data-testid="complete-profile-btn"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : (
            'Complete & Continue'
          )}
        </Button>

        <button
          type="button"
          onClick={() => {
            setStep('otp');
            setError('');
          }}
          className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to verification
        </button>
      </form>
    );
  }

  return (
    <div className="space-y-6">
      {step === 'email' ? (
        // Email Input Step
        <form onSubmit={handleSendOtp} className="space-y-4">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Mail className="h-7 w-7 text-primary" />
            </div>
            <h3 className="text-lg font-semibold">Sign in with Email</h3>
            <p className="text-sm text-muted-foreground mt-1">
              We'll send you a verification code
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="otp-email">Email address</Label>
            <Input
              id="otp-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              autoComplete="email"
              autoFocus
              disabled={loading}
            />
          </div>

          {/* Terms & Privacy Checkbox */}
          <div className="flex items-start space-x-2">
            <Checkbox 
              id="terms" 
              checked={acceptedTerms}
              onCheckedChange={setAcceptedTerms}
              disabled={loading}
            />
            <div className="flex-1">
              <label htmlFor="terms" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                I agree to the Terms & Conditions and Privacy Policy of Kosha Tax Consultants and consent to the secure processing of my information for tax services.
              </label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => window.open('/terms', '_blank')}
                >
                  Terms & Conditions
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => window.open('/privacy', '_blank')}
                >
                  Privacy Policy
                </Button>
              </div>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" className="w-full" disabled={loading || !email || !acceptedTerms}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending code...
              </>
            ) : (
              'Continue with Email'
            )}
          </Button>

          {onBack && (
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={onBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login options
            </Button>
          )}
        </form>
      ) : (
        // OTP Input Step
        <div className="space-y-4">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="h-7 w-7 text-primary" />
            </div>
            <h3 className="text-lg font-semibold">Enter verification code</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Sent to <span className="font-medium text-foreground">{email}</span>
            </p>
          </div>

          {/* Test Mode Banner - Shows OTP for development testing */}
          {testMode && testOtp && (
            <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800">
              <FlaskConical className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-200">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <span className="font-semibold">Test Mode:</span> Your OTP is{' '}
                    <code className="bg-amber-100 dark:bg-amber-900 px-2 py-0.5 rounded font-mono font-bold text-lg">
                      {testOtp}
                    </code>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                    onClick={() => {
                      navigator.clipboard.writeText(testOtp);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs mt-1 text-amber-600 dark:text-amber-400">
                  Email delivery is disabled in sandbox mode. Verify your domain on resend.com for production.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {/* OTP Input Boxes */}
          <div className="flex justify-center gap-2">
            {otp.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (otpInputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold"
                autoFocus={index === 0}
                disabled={loading}
              />
            ))}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && (
            <div className="flex items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </div>
          )}

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Didn't receive the code?{' '}
              {resendCooldown > 0 ? (
                <span className="text-muted-foreground">
                  Resend in {resendCooldown}s
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-primary hover:underline font-medium"
                  disabled={loading}
                >
                  Resend code
                </button>
              )}
            </p>
          </div>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => {
              setStep('email');
              setOtp(['', '', '', '', '', '']);
              setError('');
            }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Change email
          </Button>
        </div>
      )}

      <div className="text-center text-xs text-muted-foreground">
        <ShieldCheck className="inline h-3 w-3 mr-1" />
        Your data is protected with AES-256 encryption
      </div>

      {/* Terms & Conditions and Privacy Policy Dialog */}
      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Terms & Conditions and Privacy Policy</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-muted-foreground space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Terms & Conditions</h3>
              
              <p className="mb-2"><strong>1. Acceptance of Terms:</strong> By accessing and using Kosha Tax services, you agree to be bound by these Terms and Conditions.</p>
              
              <p className="mb-2"><strong>2. Services:</strong> Kosha Tax provides tax preparation, filing services, and LLC formation assistance.</p>
              
              <p className="mb-2"><strong>3. User Responsibilities:</strong> You are responsible for providing accurate and complete information.</p>
              
              <p className="mb-2"><strong>4. Accuracy Guarantee:</strong> If our services result in IRS penalties due to our error, we will pay those penalties.</p>
              
              <p className="mb-2"><strong>5. Confidentiality:</strong> Your data is encrypted and protected using industry-standard security measures.</p>
              
              <p className="mb-2"><strong>6. Payment:</strong> Payment is required upon completion of services. All fees are non-refundable unless required by law.</p>
              
              <p className="mb-2"><strong>7. Limitation of Liability:</strong> Kosha Tax shall not be liable for indirect, incidental, or consequential damages.</p>
            </div>
            
            <hr className="my-4" />
            
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Privacy Policy</h3>
              
              <p className="mb-2"><strong>1. Information Collected:</strong> We collect personal information including name, email, SSN (for tax filing), and financial information.</p>
              
              <p className="mb-2"><strong>2. Use of Information:</strong> Your information is used solely for tax preparation and filing services. We do not sell your data.</p>
              
              <p className="mb-2"><strong>3. Data Security:</strong> We implement AES-256 encryption for all sensitive data in transit and at rest.</p>
              
              <p className="mb-2"><strong>4. Data Retention:</strong> Tax records are retained for 7 years as required by IRS regulations.</p>
              
              <p className="mb-2"><strong>5. Third-Party Sharing:</strong> We only share information with IRS and state tax authorities as necessary for filing.</p>
              
              <p className="mb-2"><strong>6. Your Rights:</strong> You have the right to access, correct, or delete your personal information.</p>
              
              <p className="mb-2"><strong>7. Contact:</strong> For inquiries, contact privacy@koshatax.com</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default OTPLogin;
